import { useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { useToast } from '../ui/toast';
import {
  createReview,
  type MyPurchase,
  type MyReview,
} from '../../lib/apiClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

/**
 * The Settings tab's **Share Your Feedback**, copied from production.
 *
 * The section appears only when the customer has a completed purchase they have
 * not reviewed. It opens as a single button; the form replaces it on click.
 *
 * Production targets the **last** unreviewed purchase in the list, which is the
 * one this reproduces.
 */
export function ShareYourFeedback({
  purchasesNeedingReview,
  onReviewed,
}: {
  purchasesNeedingReview: MyPurchase[];
  onReviewed: (review: MyReview) => void;
}) {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (purchasesNeedingReview.length === 0) return null;

  // Production takes the last of the list.
  const target = purchasesNeedingReview[purchasesNeedingReview.length - 1];

  function reset() {
    setOpen(false);
    setRating(0);
    setBody('');
    setDisplayName('');
    setConsent(false);
  }

  async function submit() {
    if (!token || saving) return;
    if (rating === 0) {
      setFailure('Please select a rating before submitting.');
      return;
    }
    const resultId = target?.resultId;
    if (!resultId) {
      setFailure('Error submitting review');
      return;
    }

    setSaving(true);
    setFailure(null);

    const result = await createReview(token, {
      resultId,
      rating,
      body,
      displayName: displayName.trim() || undefined,
      consentToPublish: consent,
    });
    setSaving(false);

    if (result.state !== 'ok') {
      setFailure(result.message);
      return;
    }
    reset();
    onReviewed(result.review);
    // Production's confirmation. The form closing on its own is ambiguous —
    // it looks the same as cancelling.
    toast({ title: 'Thank you for your feedback!' });
  }

  return (
    <section
      className="p-6 rounded-xl border border-primary/30 bg-primary/5 space-y-5"
      data-testid="share-feedback"
    >
      <div className="space-y-1">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Star className="w-5 h-5 text-primary" aria-hidden="true" />
          Share Your Feedback
        </h2>
        <p className="text-sm text-muted-foreground">
          If High Performer OS has been useful, we would appreciate your feedback.
        </p>
      </div>

      {!open ? (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="review-open">
          Write a Review
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Your rating</p>
            <StarRating value={rating} onChange={setRating} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="review-body">
              What was most useful? What would you improve?
            </label>
            <Textarea
              id="review-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Share your honest thoughts on the report and workbook..."
              data-testid="review-body"
              className="bg-background min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="review-display-name">
              Display name (optional)
            </label>
            <Input
              id="review-display-name"
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="e.g. Sarah M., or leave blank for anonymous"
              data-testid="review-display-name"
              className="bg-background"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              data-testid="review-consent"
              className="mt-0.5 h-4 w-4 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <span className="text-sm text-muted-foreground">
              I agree to let High Performer OS display this review publicly as a testimonial. My
              display name (if provided) may be shown alongside my review.
            </span>
          </label>

          <div className="flex gap-3">
            <Button
              onClick={() => void submit()}
              disabled={saving || rating === 0}
              data-testid="review-submit"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Submit Review
            </Button>
            <Button variant="outline" onClick={reset} data-testid="review-cancel">
              Cancel
            </Button>
          </div>

          {failure ? (
            <p className="text-sm font-medium" data-testid="review-error" role="status">
              {failure}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/**
 * Production's five-star control: filled up to the hovered star, or up to the
 * chosen one when nothing is hovered.
 */
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1" data-testid="review-rating">
      {[1, 2, 3, 4, 5].map((star) => {
        const lit = (hovered || value) >= star;
        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
            aria-pressed={value >= star}
            data-testid={`review-star-${star}`}
            data-lit={lit ? 'true' : 'false'}
            className="p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Star
              className={`w-6 h-6 ${lit ? 'text-primary' : 'text-muted-foreground'}`}
              fill={lit ? 'currentColor' : 'none'}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}
